import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerLog } from '../services/customerLog.service';
import { Button, Icon, LogEntry, PlusIcon, SaveIcon, Spinner } from './core';
import { tsToDate } from '../utils/timestamp';

const MAX_NOTE_LENGTH = 1000;

interface ActivityLogSectionProps {
  logs: CustomerLog[];
  currentUserId?: string;
  /** True when the current user has customers.delete permission (admin). Allows deleting any entry. */
  canDeleteAny?: boolean;
  onAddNote: (_text: string) => Promise<void>;
  onEditNote?: (_logId: string, _newMessage: string) => Promise<void>;
  onDeleteNote?: (_logId: string) => Promise<void>;
  /** Called when the user taps "Set Reminder" on a log entry. Receives the log message as description. */
  onRemind?: (_logMessage: string) => void;
  onViewAll: () => void;
}

export function ActivityLogSection({
  logs,
  currentUserId,
  canDeleteAny,
  onAddNote,
  onEditNote,
  onDeleteNote,
  onRemind,
  onViewAll,
}: ActivityLogSectionProps) {
  const { t } = useTranslation();
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (showNoteForm) {
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [showNoteForm]);

  async function handleSave() {
    const trimmed = noteText.trim();
    if (!trimmed) return;
    if (trimmed.length > MAX_NOTE_LENGTH) {
      setNoteError(t('customers.noteTooLong'));
      return;
    }
    setSaving(true);
    setNoteError(null);
    try {
      await onAddNote(trimmed);
      setNoteText('');
      setShowNoteForm(false);
    } catch (err) {
      console.error('[ActivityLogSection] save note error:', err);
      setNoteError(t('customers.logError'));
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setShowNoteForm(false);
    setNoteText('');
    setNoteError(null);
  }

  return (
    <div className="mt-5">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="font-display text-lg font-semibold tracking-tight text-green-900">
          {t('customers.activityLog')}
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="font-body text-sm text-green-700 hover:text-green-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
            onClick={onViewAll}
          >
            {t('customers.viewAllLogs')}
          </button>
          {!showNoteForm && (
            <Button size="sm" variant="secondary" onClick={() => setShowNoteForm(true)}>
              <Icon size="sm"><PlusIcon /></Icon>
              {t('customers.addNote')}
            </Button>
          )}
        </div>
      </div>

      {/* Add note form */}
      {showNoteForm && (
        <div className="mb-4 rounded-sm border-thick border-dashed border-green-400 bg-white p-4">
          <textarea
            ref={textareaRef}
            rows={3}
            maxLength={MAX_NOTE_LENGTH}
            placeholder={t('customers.notePlaceholder')}
            aria-label={t('customers.noteLabel')}
            value={noteText}
            onChange={e => {
              setNoteText(e.target.value);
              if (noteError) setNoteError(null);
            }}
            disabled={saving}
            className="w-full resize-none bg-transparent font-body text-sm text-gray-900 outline-none placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
          />
          {noteError && (
            <p className="mt-1 font-body text-xs text-red-600">{noteError}</p>
          )}
          <div className="mt-2.5 flex items-center gap-2 border-t border-green-100 pt-2.5">
            <span className="me-auto font-body text-xs text-gray-400">
              {noteText.length}/{MAX_NOTE_LENGTH}
            </span>
            <Button size="sm" variant="ghost" onClick={handleCancel} disabled={saving}>
              {t('customers.cancelNote')}
            </Button>
            <Button size="sm" variant="primary" onClick={handleSave} disabled={saving || !noteText.trim()}>
              {saving ? <Spinner size="sm" /> : <Icon size="sm"><SaveIcon /></Icon>}
              {t('customers.saveNote')}
            </Button>
          </div>
        </div>
      )}

      {/* Log entries */}
      {logs.length === 0 ? (
        <p className="font-body text-sm text-gray-400">{t('customers.noLogs')}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {logs.map(log => {
            const isAuthor = !!currentUserId && currentUserId === log.createdBy;
            const canDelete = isAuthor || !!canDeleteAny;
            return (
              <LogEntry
                key={log.id}
                message={log.message}
                authorInitials={(log.userDisplayName ?? '?')[0].toUpperCase()}
                authorName={log.userDisplayName ?? ''}
                timestamp={tsToDate(log.createdAt) ?? new Date()}
                compact
                isEdited={!!log.updatedAt}
                onSave={isAuthor && onEditNote ? (newMsg) => onEditNote(log.id, newMsg) : undefined}
                onDelete={canDelete && onDeleteNote ? () => onDeleteNote(log.id) : undefined}
                onRemind={onRemind ? () => onRemind(log.message) : undefined}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
